/**
 * Valider un bon d'intervention : ce que la validation ÉCRIRA, puis la confirmation (lot I1.2, ADR-0014).
 *
 * Le récapitulatif vient du serveur, qui joue la VRAIE validation dans une transaction qu'il annule : il ne peut pas
 * diverger de ce qui sera écrit, refus compris. Un bon qui ne peut pas être validé (incomplet, droit d'écrire
 * manquant, entretien déjà porté par un autre bon) montre le refus du serveur ICI, dans le panneau, et la
 * confirmation reste grisée.
 *
 * La confirmation envoie la révision lue avec le bon : un écran périmé ne valide pas un contenu modifié entre-temps.
 * Aucune règle métier ici : l'écran ne fait que montrer ce que le serveur annonce.
 */
import { useEffect, useState } from 'react';

import { ApiError, api, type ApercuValidationBon, type BonDetail, type FacturationBon } from '../api/client';
import { LIBELLE_FACTURATION } from '../domaine/bon-modeles';
import { choixValidation, lignesApercu, pointagesIncomplets } from '../domaine/validation-bon';
import { ErreurFormulaire, Panneau } from './Formulaire';

export function ValidationBon({
  bon,
  onFermer,
  onValide,
}: {
  bon: BonDetail;
  onFermer: () => void;
  /** Le bon validé, tel que le serveur le rend : la page annonce ce qui a été écrit puis relit. */
  onValide: (valide: BonDetail) => void;
}) {
  const [apercu, setApercu] = useState<ApercuValidationBon | null>(null);
  const [refus, setRefus] = useState<ApiError | Error | null>(null);
  const [enCours, setEnCours] = useState(false);
  // « Contrat annuel / Facturé à la visite » quand le bon clôturé sur le téléphone ne l’a pas : jamais présélectionné.
  const [facturation, setFacturation] = useState<FacturationBon | null>(null);
  const choix = choixValidation(apercu, facturation);

  useEffect(() => {
    let annule = false;
    setApercu(null);
    setRefus(null);
    setFacturation(null);
    api
      .apercuValidationBon(bon.id)
      .then((a) => {
        if (!annule) setApercu(a);
      })
      .catch((e: unknown) => {
        if (!annule) setRefus(e instanceof Error ? e : new Error('Le récapitulatif n’a pas pu être calculé.'));
      });
    return () => {
      annule = true;
    };
  }, [bon.id, bon.revision]);

  const valider = async () => {
    setEnCours(true);
    setRefus(null);
    try {
      onValide(await api.validerBon(bon.id, bon.revision, choix.billing));
    } catch (e) {
      setRefus(e instanceof Error ? e : new Error('La validation a échoué.'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Panneau
      titre={`Valider le bon ${bon.number}`}
      sousTitre="Ce que la validation écrira dans la machine. Une fois validé, le bon est figé."
      onFermer={onFermer}
      actions={
        <>
          <button type="button" className="secondaire" disabled={enCours} onClick={onFermer}>
            Pas maintenant
          </button>
          <button type="button" disabled={!choix.confirmable || enCours} onClick={() => void valider()}>
            {enCours ? 'Validation…' : 'Valider ce bon'}
          </button>
        </>
      }
    >
      {apercu === null && refus === null && <p className="aide">Calcul de ce qui sera écrit…</p>}
      <ErreurFormulaire message={refus?.message ?? null} details={refus instanceof ApiError ? refus.details : undefined} />
      {apercu && (
        <>
          {apercu.billingRequired && (
            <div className="choix-facturation-validation">
              <p className="recap-attention">
                Ce bon a été clôturé sur le téléphone sans « Contrat annuel / Facturé à la visite » : choisissez avant de valider.
              </p>
              <div className="rangee-boutons" role="radiogroup" aria-label="Contrat annuel ou facturé à la visite">
                {(['CONTRACT', 'BILLABLE'] as const).map((f) => (
                  <button
                    key={f}
                    type="button"
                    role="radio"
                    aria-checked={facturation === f}
                    className={facturation === f ? undefined : 'secondaire'}
                    disabled={enCours}
                    onClick={() => setFacturation(f)}
                  >
                    {LIBELLE_FACTURATION[f]}
                  </button>
                ))}
              </div>
            </div>
          )}
          <ul className="recap-validation">
            {pointagesIncomplets(bon.pointages ?? []).map((texte) => (
              <li key={texte} className="recap-attention">
                {texte}
              </li>
            ))}
            {lignesApercu(apercu).map((ligne, i) => (
              <li key={i} className={ligne.gravite === 'attention' ? 'recap-attention' : undefined}>
                {ligne.texte}
              </li>
            ))}
          </ul>
          <p className="aide">
            Tout s’écrit ensemble, ou rien ne s’écrit. Une heure fausse se corrige ensuite en rectifiant le relevé ou
            l’entretien depuis la fiche de la machine : rien ne s’efface.
          </p>
        </>
      )}
    </Panneau>
  );
}
